/**
 * Image Quality Worker Manager
 * Handles web worker lifecycle and provides fallback to synchronous calculation
 */

interface WorkerResponse {
  type: 'sharpnessResult' | 'error';
  sharpness?: number;
  message?: string;
}

// Singleton worker instance for reuse
let workerInstance: Worker | null = null;
let isWorkerSupported: boolean | null = null;

/**
 * Check if Web Workers are supported in this browser
 */
function checkWorkerSupport(): boolean {
  if (isWorkerSupported !== null) {
    return isWorkerSupported;
  }

  try {
    isWorkerSupported = typeof Worker !== 'undefined';
    return isWorkerSupported;
  } catch {
    isWorkerSupported = false;
    return false;
  }
}

/**
 * Get or create the worker instance
 */
function getWorker(): Worker | null {
  if (!checkWorkerSupport()) {
    return null;
  }

  if (workerInstance) {
    return workerInstance;
  }

  try {
    // Vite-specific worker import syntax
    workerInstance = new Worker(new URL('../workers/imageQuality.worker.ts', import.meta.url), { type: 'module' });
    return workerInstance;
  } catch (error) {
    console.warn('Failed to create image quality worker:', error);
    isWorkerSupported = false;
    return null;
  }
}

/**
 * Cleanup worker instance (call this when done or on page unload)
 */
export function cleanupWorker(): void {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
  }
}

/**
 * Synchronous fallback: Calculate sharpness on main thread
 * This is the original implementation preserved for compatibility
 */
export function calculateSharpnessSync(imageData: ImageData): number {
  const { data, width, height } = imageData;
  let sum = 0;
  let count = 0;

  // Iterate through each pixel (excluding border pixels)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;

      // Get center pixel and 4 neighbors (using red channel)
      const center = data[idx];
      const top = data[((y - 1) * width + x) * 4];
      const bottom = data[((y + 1) * width + x) * 4];
      const left = data[(y * width + (x - 1)) * 4];
      const right = data[(y * width + (x + 1)) * 4];

      // Calculate Laplacian operator
      const laplacian = Math.abs(4 * center - top - bottom - left - right);
      sum += laplacian;
      count++;
    }
  }

  // Return variance (average)
  return count > 0 ? sum / count : 0;
}

/**
 * Calculate sharpness using web worker (with timeout and fallback)
 */
function calculateSharpnessWithWorker(imageData: ImageData, timeoutMs = 5000): Promise<number> {
  return new Promise((resolve, reject) => {
    const worker = getWorker();

    if (!worker) {
      // Worker not available, reject to trigger fallback
      reject(new Error('Web Worker not available'));
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    let messageHandler: (event: MessageEvent<WorkerResponse>) => void;
    let errorHandler: (error: ErrorEvent) => void;

    // Cleanup function
    const cleanup = () => {
      clearTimeout(timeoutId);
      worker.removeEventListener('message', messageHandler);
      worker.removeEventListener('error', errorHandler);
    };

    // Set up timeout
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Worker timeout'));
    }, timeoutMs);

    // Handle worker response
    messageHandler = (event: MessageEvent<WorkerResponse>) => {
      const { type, sharpness, message } = event.data;

      if (type === 'sharpnessResult' && typeof sharpness === 'number') {
        cleanup();
        resolve(sharpness);
      } else if (type === 'error') {
        cleanup();
        reject(new Error(message || 'Worker calculation failed'));
      }
    };

    // Handle worker error
    errorHandler = (error: ErrorEvent) => {
      cleanup();
      reject(new Error(error.message || 'Worker error'));
    };

    // Attach event listeners
    worker.addEventListener('message', messageHandler);
    worker.addEventListener('error', errorHandler);

    // Send imageData to worker
    // Note: We clone the data instead of transferring to preserve it for fallback
    worker.postMessage({
      type: 'calculateSharpness',
      imageData: {
        data: imageData.data,
        width: imageData.width,
        height: imageData.height,
      },
    });
  });
}

/**
 * Calculate image sharpness (main entry point)
 * Automatically uses web worker if available, falls back to sync calculation
 */
export interface SharpnessResult {
  sharpness: number;
  source: 'worker' | 'sync';
  workerError?: string;
  durationMs: number;
}

export async function calculateSharpness(imageData: ImageData): Promise<SharpnessResult> {
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const elapsed = () => Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - started);

  try {
    const sharpness = await calculateSharpnessWithWorker(imageData);
    return { sharpness, source: 'worker', durationMs: elapsed() };
  } catch (error) {
    const workerError = error instanceof Error ? error.message : 'Unknown error';
    return {
      sharpness: calculateSharpnessSync(imageData),
      source: 'sync',
      workerError,
      durationMs: elapsed(),
    };
  }
}

// Cleanup worker on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanupWorker);
}
